import { FilterParam } from '../types';
import buildBaseEndpoint from './buildBaseEndpoint';

function getDownloadColumnsMap() {
  const keyValue: [string, string][] = [
    // disease
    ['numVariantsAssociatedToDisease', 'num_variants'],
    ['numGenesAssociatedToDisease', 'num_genes'],
    ['disease_prevalence_class', 'disease_prevalence_class'],
    ['disease_prevalence_type', 'disease_prevalence_type'],
    ['disease_prevalence_geo_area', 'disease_prevalence_geo_area'],
    ['disease_inheritance', 'disease_inheritance'],
    ['numChemicals', 'numChemicals'],
    // drugs
    ['chemID', 'chem_cuis'],
    ['chemPrefName', 'chem_name'],
    ['chemMoleculeType', 'chemMoleculeType'],
    // gda/summary
    ['chemIDs', 'chem_cuis'],
    ['diseaseUMLSCUI', 'disease_cui'],
    ['geneNcbiID', 'gene_ncbi'],
    ['geneDSI', 'dsi'],
    ['geneDPI', 'dpi'],
    ['genepLI', 'pli'],
    ['EL', 'el'],
    ['EI', 'ei'],
    ['geneProteinClassNames', 'gp_class_names'],
    // gda/evidences
    ['associationType', 'association_type'],
    // vda/summary
    ['variantStrID', 'variant_name'],
    ['variantDSI', 'dsi'],
    ['variantDPI', 'dpi'],
    ['diseaseName', 'disease_name'],
    ['geneNcbiIDs', 'gene_ncbi'],
    ['gene_ensembl_id', 'gene_ensembl_id'],

    // vda/evidences
    ['diseaseType', 'disease_type'],
    ['geneSymbol', 'gene_symbol'],
    ['mostSevereConsequences', 'v_msc'],
    ['sentenceHTML', 'sentence_html'], //'sentence_text'
    ['clinical_significance', 'clinical_significance'],
    ['association', 'association'],
    ['citation_count', 'citation_count'],

    // genes/GDAs/summary
    ['diseaseClassName_MSH', 'disease_msh'],
    ['diseaseClassName_UMLS', 'disease_umls'],
    ['diseaseClassName_DO', 'disease_do'],
    ['diseaseClassName_HPO', 'disease_hpo'],
    // variants/all
    ['strID', 'variant'],
    ['DSI', 'dsi'],
    ['DPI', 'dpi'],
    ['DBSNPclass', 'class'],
    ['Alleles', 'alleles'],
    ['genome', 'genome'],
    ['exome', 'exome'],
    ['numDiseasesAssociatedToVariant', 'num_diseases'],
    ['numPublications', 'num_publications'],
    ['firstRef', 'first_ref'],
    ['lastRef', 'last_ref'],
    ['gene_ncbi_id', 'gene_ncbi_id'],
    ['gene_ensembl_id', 'gene_ensembl_id'],
    // gene/all
    ['proteins', 'protein'],
    ['PLI', 'pli'],
    ['ncbiID', 'gene_ncbi'],
    ['proteinClassIDs', 'protein_class'],
    ['pathwayName', 'pathway_name'],
    ['pathwayID', 'pathway_id'],
    // gda/all
    ['geneProteinStrIDs', 'genep_str_ids'],
    //
    ['score', 'score'],
    ['numPMIDsupportingAssociation', 'num_pmid_assoc'],
    ['numberChemsIncludedInEvidence', 'numberChemsIncludedInEvidence'],
    ['numberPmidsWithChemsIncludedInEvidence', 'num_pmids_evid'],
    ['numberNCTSWithChemsIncludedInEvidence', 'numberNCTSWithChemsIncludedInEvidence'],
    ['numDBSNPsupportingAssociation', 'num_var_dis'],
    ['yearInitial', 'year_initial'],
    ['yearFinal', 'year_final'],
    ['ei', 'ei'],
    ['geneNcbiType', 'gene_type'],
    [' reference', 'reference'],
    ['reference_type', 'reference_type'],
  ];

  return new Map(keyValue);
}

export function buildDownloadEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  let filterParam: FilterParam | null = null;
  switch (view) {
    case 'GENES':
      filterParam = 'gene_ncbi_id';
      break;
    case 'DISEASES':
      filterParam = 'disease';
      break;
    case 'VARIANTS':
      filterParam = 'variant';
      break;
    case 'CHEMICALS':
      filterParam = 'chem_ids_init';
      break;
  }

  // The backend idents value:
  const backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  const paramsMap = {
    source: source,
    ...(filterParam && { [filterParam]: filterValue }),
    col_sel: col_sel,
  };

  // Construct the endpoint URL
  const queryParams = new URLSearchParams();

  Object.entries(paramsMap).forEach(([param, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(param, value);
    }
  });

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${queryParams.toString().replace(/%2C/g, ',')}`;
  // override for chem downloads
  // the format for chem downloads is inconsitent with the other endpoints
  // 1 solution for the backend could be to remove "/all" from the url patterns
  // alternatively, nest tsv/excel under /all instead of as a sibling
  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}
/*
random patterns are still missing -> tsv?source=ALL&disease=UMLS_C0028754&min_score=0.9950&max_score=1.0050&col_sel=variant_name,gene_symbol
&min_score=0.9950&max_score=1.0050&

/api/gda/summary/tsv?source=ALL&disease=UMLS_C0028754&col_sel=disease_name
/api/gda/summary/tsv?source=ALL&disease=UMLS_C0028754&col_sel=disease_name
*/

//TODO: Replace the function above fot this one in TR-116
export function buildDownloadFiltersEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
  filters = {},
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  let filterParam: FilterParam | null = null;
  switch (view) {
    case 'GENES':
      filterParam = 'gene_ncbi_id';
      break;
    case 'DISEASES':
      filterParam = 'disease';
      break;
    case 'VARIANTS':
      filterParam = 'variant';
      break;
    case 'CHEMICALS':
      filterParam = 'chem_ids_init';
      break;
  }

  // The backend idents value:
  const backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  const paramsMap = {
    source: source,
    ...(filterParam && { [filterParam]: filterValue }),
    col_sel: col_sel,
    ...filters,
  };

  // Construct the endpoint URL
  const queryParams = new URLSearchParams();

  Object.entries(paramsMap).forEach(([param, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(param, value);
    }
  });

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${queryParams.toString().replace(/%2C/g, ',')}`;
  // override for chem downloads
  // the format for chem downloads is inconsitent with the other endpoints
  // 1 solution for the backend could be to remove "/all" from the url patterns
  // alternatively, nest tsv/excel under /all instead of as a sibling
  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}
